import { useAuth0 } from "@auth0/auth0-react";
import { CircularProgress } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { LoginContainer } from "components/LoginContainer";
import { Auth0LoginMutationVariables } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { loadJobVisitsInit } from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { loginAuth0, setAuth } from "store/slices/user.store";

export const Auth0Callback: FC = () => {
  const { authVar, auth0Var } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState("");
  const auth0Props = JSON.parse(localStorage.getItem("auth0Props") || "{}");
  const [url] = useState(auth0Var.url || auth0Props.url || "");
  const [callbackHandled, setCallbackHandled] = useState(false);

  const handleLoginError = useCallback(
    (e: any) => {
      console.error(e);
      navigate("/login");
      switch (e[0].message) {
        case "no-mobile-license":
          dispatch(addSnackbarMessage({ key: "Login-failed-noMobileLicense" }));
          break;
        default:
          dispatch(addSnackbarMessage({ key: "Login-failed" }));
          break;
      }
    },
    [navigate, dispatch],
  );

  const auth0Login = useCallback(
    async (variables: Auth0LoginMutationVariables) => {
      await dispatch(loginAuth0(variables))
        .then(unwrapResult)
        .then(() => {
          localStorage.setItem("lastSuccessfulLogin", variables.email);
          dispatch(loadJobVisitsInit());
          navigate("/jobs");
        })
        .catch((e) => {
          handleLoginError(e);
        });
    },
    [dispatch, handleLoginError, navigate],
  );

  const { getAccessTokenSilently, isLoading, error, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (isLoading) return;
    if (error) {
      console.log(error);
      dispatch(
        setAuth({
          ...authVar,
          message: "Auth0 failed",
          //message: error.message,
        }),
      );
      handleLoginError(error);
    } else if (isAuthenticated) {
      const handleResult = async () => {
        let { audience: a } = auth0Var;
        let token = await getAccessTokenSilently({
          audience: a || "",
          detailedResponse: true,
        });
        if (!token) throw Error("No token received");
        setAccessToken(token.access_token);
        setCallbackHandled(true);
      };
      handleResult();
    }
  }, [
    auth0Var,
    authVar,
    callbackHandled,
    dispatch,
    error,
    getAccessTokenSilently,
    handleLoginError,
    isAuthenticated,
    isLoading,
  ]);

  const getRefreshToken = useCallback((): string => {
    // ! If you're not getting the correct data, double check what is actually in the local store and what you're fetching
    // ! console.log everything, there might be two entries that looks the same, but aren't
    const entry = Object.entries(localStorage).find((key) => key[0].includes("auth0spajs"));

    if (entry) {
      const body = JSON.parse(entry[1]);
      return body?.body?.refresh_token ?? "";
    }

    return "";
  }, []);

  useEffect(() => {
    if (accessToken) {
      // ! Keep in mind, if any of these values are missing, it WILL break
      auth0Login({
        url: url,
        email: auth0Var.email,
        accessToken: accessToken,
        refreshToken: getRefreshToken(),
      });
    }
  }, [accessToken, url, auth0Login, auth0Var.email, getRefreshToken]);

  return (
    <LoginContainer>
      <CircularProgress size={24} />
    </LoginContainer>
  );
};
